import Session from '../core/session'
import Config from '../core/config'

interface ClickData {
  element: string
  selector: string
  text: string
  coordinates: { x: number; y: number }
  timestamp: number
  clickCount: number
}

export class InteractionCollector {
  private static instance: InteractionCollector
  private isTracking: boolean = false
  private lastClick: ClickData | null = null
  private clickTimeout: number | null = null
  private scrollTimeout: number | null = null
  private lastScrollDepth: number = 0

  private constructor() {}

  public static getInstance(): InteractionCollector {
    if (!InteractionCollector.instance) {
      InteractionCollector.instance = new InteractionCollector()
    }
    return InteractionCollector.instance
  }

  /**
   * Start tracking interactions
   */
  public start(): void {
    if (!Config.get('captureClicks')) {
      return
    }

    if (this.isTracking) {
      return
    }

    this.isTracking = true

    // Track clicks
    document.addEventListener('click', this.handleClick.bind(this), true)

    // Track scrolls
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true })
  }

  /**
   * Handle click events
   */
  private handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement
    if (!target) return

    const selector = this.getElementSelector(target)
    const text = this.getElementText(target)
    const now = Date.now()

    // Detect rage clicks (multiple clicks in short time)
    let clickCount = 1
    let timeSinceLastClick = 0

    if (this.lastClick && this.lastClick.selector === selector) {
      timeSinceLastClick = now - this.lastClick.timestamp
      
      // If clicked within 1 second, increment count
      if (timeSinceLastClick < 1000) {
        clickCount = this.lastClick.clickCount + 1
      }
    }

    const clickData: ClickData = {
      element: target.tagName.toLowerCase(),
      selector: selector,
      text: text,
      coordinates: { x: event.clientX, y: event.clientY },
      timestamp: now,
      clickCount: clickCount
    }

    this.lastClick = clickData

    // Clear previous timeout
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout)
    }

    // Send click event after 100ms (debounced)
    this.clickTimeout = window.setTimeout(() => {
      Session.trackEvent('click', {
        element: clickData.element,
        selector: clickData.selector,
        text: clickData.text,
        coordinates: clickData.coordinates,
        click_count: clickData.clickCount,
        time_since_last_click: timeSinceLastClick
      })

      if (Config.isDebug() && clickData.clickCount >= 3) {
        console.warn('[RadarSnap] Rage click detected:', clickData.selector)
      }
    }, 100)
  }

  /**
   * Handle scroll events
   */
  private handleScroll(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout)
    }

    // Debounce scroll events
    this.scrollTimeout = window.setTimeout(() => {
      const scrollDepth = this.calculateScrollDepth()

      // Only track significant scroll changes (> 10%)
      if (Math.abs(scrollDepth - this.lastScrollDepth) > 10) {
        Session.trackEvent('scroll', {
          depth: scrollDepth,
          direction: scrollDepth > this.lastScrollDepth ? 'down' : 'up',
          page_height: document.documentElement.scrollHeight,
          viewport_height: window.innerHeight
        })

        this.lastScrollDepth = scrollDepth
      }
    }, 500)
  }

  /**
   * Calculate scroll depth percentage
   */
  private calculateScrollDepth(): number {
    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollableHeight = documentHeight - windowHeight
    const scrollPercentage = (scrollTop / scrollableHeight) * 100

    return Math.min(Math.round(scrollPercentage), 100)
  }

  /**
   * Get CSS selector for element
   */
  private getElementSelector(element: HTMLElement): string {
    if (element.id) {
      return `#${element.id}`
    }

    if (element.className) {
      const classes = element.className.trim().split(/\s+/).join('.')
      return `${element.tagName.toLowerCase()}.${classes}`
    }

    return element.tagName.toLowerCase()
  }

  /**
   * Get text content of element
   */
  private getElementText(element: HTMLElement): string {
    // Get direct text content (not from children)
    let text = ''

    if (element.textContent) {
      text = element.textContent.trim().substring(0, 100) // Limit to 100 chars
    } else if (element instanceof HTMLInputElement) {
      text = element.placeholder || element.value || ''
    } else if (element instanceof HTMLButtonElement) {
      text = element.innerText || ''
    }

    return text
  }

  /**
   * Stop tracking
   */
  public stop(): void {
    this.isTracking = false
    document.removeEventListener('click', this.handleClick.bind(this), true)
    window.removeEventListener('scroll', this.handleScroll.bind(this))
  }
}

export default InteractionCollector.getInstance()